import { LucidePlayCircle } from "lucide-react";
import { FiShield, FiCpu, FiActivity, FiZap, FiLock, FiRepeat, FiSearch, FiSettings, FiTerminal, FiPlayCircle, FiBarChart, FiCheckSquare } from "react-icons/fi";
import {
  LuBug,
  LuBuilding2,
  LuClipboardList,
  LuClock,
  LuFileText,
  LuLayoutDashboard,
  LuMousePointerClick,
  LuPalette,
  LuRefreshCw,
  LuRocket,
  LuShoppingBag,
  LuSmartphone,
  LuUsers,
  LuWallet,
  LuWrench,
} from "react-icons/lu";

export const testingBenefits = [
  {
    icon: <FiShield className="w-8 h-8" />,
    title: "Zero-Risk Deployment",
    desc: "Rigorous regression and sanity testing ensure new updates never compromise existing system stability.",
  },
  {
    icon: <FiCpu className="w-8 h-8" />,
    title: "Optimized Performance",
    desc: "Load and stress testing to ensure your platform handles 10x traffic spikes without latency.",
  },
  {
    icon: <FiLock className="w-8 h-8" />,
    title: "Security Hardening",
    desc: "Deep vulnerability assessments and penetration testing to keep your data safe from evolving threats.",
  },
  {
    icon: <FiRepeat className="w-8 h-8" />,
    title: "Proactive Maintenance",
    desc: "Continuous monitoring and L3 support to resolve infrastructure issues before they impact users.",
  },
  {
    icon: <FiActivity className="w-8 h-8" />,
    title: "99.9% Uptime Guarantee",
    desc: "Strategic server patching and database optimization to ensure your business stays online 24/7.",
  },
];

export const testingServices = [
  {
    icon: <FiPlayCircle size={36} color="#34d399" />,
    title: "Automated Software Regression Testing",
    description: "Ensure application stability with automated testing and continuous validation across every code change.",
    image: "/images/TestingMaintenance/AutomatedRegressionTesting.png",
    link: "/automated-regression-testing-india",
    points: ["End-to-End Test Automation", "Functional Workflow Testing", "Continuous Testing Support"],
  },
  {
    icon: <FiTerminal size={36} color="#f59e0b" />,
    title: "Application Security & Penetration Testing",
    description: "Identify vulnerabilities and strengthen application security with advanced ethical hacking and security audits.",
    image: "/images/TestingMaintenance/SecurityPenetrationTesting.png",
    link: "/security-testing-india",
    points: ["Security Vulnerability Testing", "API & Database Security Checks", "Penetration Testing Solutions"],
  },
  {
    icon: <FiZap size={36} color="#a855f7" />,
    title: "Performance & Load Testing Services",
    description: "Measure application speed, scalability, and performance under high user traffic with detailed analysis.",
    image: "/images/TestingMaintenance/PerformanceLoadTesting.png",
    link: "/performance-testing-india",
    points: ["Load & Stress Testing", "Performance Optimization", "Server & Response Analysis"],
  },
  // {
  //   icon: <FiSearch size={36} color="#10b981" />,
  //   title: "Manual QA & Bug Hunting",
  //   description: "Expert exploratory testing to catch UI/UX issues, edge cases, and usability problems that automation often misses.",
  //   image: "/images/TestingMaintenance/ManualQABugHunting.png",
  //   link: "/manual-qa-india",
  //   points: ["Exploratory Testing", "UI/UX Consistency Checks", "Edge Case Validation"],
  // },
];

export const testingProcessSteps = [
  {
    step: "01",
    title: "Audit & Strategy",
    description: "We analyze your current codebase and requirements to define a risk-based testing roadmap.",
    points: ["Requirement analysis", "Risk assessment", "Test environment setup"],
  },
  {
    step: "02",
    title: "Test Scripting",
    description: "Development of comprehensive manual test cases and automated scripts for maximum coverage.",
    points: ["Automated script development", "Data set creation", "Scenario mapping"],
  },
  {
    step: "03",
    title: "Execution & Analysis",
    description: "Deep testing across browsers and devices to identify performance bottlenecks and functional bugs.",
    points: ["Multi-browser testing", "Vulnerability scanning", "Log analysis"],
  },
  {
    step: "04",
    title: "Fix & Release Support",
    description: "We work directly with dev teams to verify fixes and ensure a seamless, bug-free production launch.",
    points: ["Regression testing", "Final QA sign-off", "Post-release monitoring"],
  },
];

export const testingFaqs = [
  {
    q: "Why should we invest in automated testing?",
    a: "Automated testing significantly reduces the time-to-market and prevents costly human errors during repetitive regression checks, ensuring your software is always release-ready.",
  },
  {
    q: "How often should software maintenance be performed?",
    a: "We recommend monthly maintenance for minor updates and patches, with comprehensive security audits every quarter to protect against new digital vulnerabilities.",
  },
  {
    q: "Can you handle testing for legacy applications?",
    a: "Yes. We specialize in stabilizing older applications by identifying technical debt, improving test coverage, and modernizing infrastructure without breaking existing features.",
  },
  {
    q: "Do you offer security compliance testing?",
    a: "Absolutely. We conduct audits tailored to specific standards like OWASP Top 10, ensuring your platform meets high-security benchmarks.",
  },
  {
    q: "What is your bug reporting process?",
    a: "We use tools like Jira or ClickUp for real-time tracking, providing detailed bug reports that include reproduction steps, logs, and recommended fixes.",
  },
];

export const testingTechStack = ["Selenium", "Cypress", "Playwright", "Postman", "JMeter", "Jest", "Appium", "Docker", "Sentry", "New Relic", "Jenkins", "GitHub Actions"];

export const testingTechIcons = {
  Selenium: "🧪",
  Cypress: "🌲",
  Playwright: "🎭",
  Postman: "🚀",
  JMeter: "⚖️",
  Jest: "🃏",
  Appium: "📱",
  Docker: "🐳",
  Sentry: "🛡️",
  "New Relic": "📈",
  Jenkins: "🏗️",
  "GitHub Actions": "🤖",
};

export const testingHeroData = {
  backgroundImage: "/images/serviceBanners/TestingLatest.webp",
  whiteImage: "/images/serviceBanners/TestingLatestW.webp",
  subTitle: "Software Testing & Maintenance Services",
  title: (
    <>
      Ensure Reliable <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-600">Performance & Seamless </span> <br /> User Experience
    </>
  ),
  description:
    "We provide software testing and maintenance services focused on performance, security, stability, and long-term reliability. Our testing and support solutions help businesses identify issues early, improve application quality, reduce downtime, and ensure smooth performance across web, mobile, and enterprise platforms.",
  primaryButton: {
    title: "Get Started",
    onClick: () => console.log("Get Started clicked"),
  },
  secondaryButton: {
    title: "View Our Work",
    onClick: () => console.log("View Our Work clicked"),
  },
  featureData: [
    {
      icon: "/images/services/website/high_performance.webp",
      title: "Performance Optimization",
      desc: "Ensure fast, stable, and high-performing digital applications.",
      iconColor: "#a855f7",
    },
    {
      icon: "/images/services/website/responsive.webp",
      title: "Cross-Platform Compatibility",
      desc: "Smooth functionality across devices, browsers, and platforms.",
      iconColor: "#22d3ee",
    },
    {
      icon: "/images/services/website/secure.webp",
      title: "Secure & Reliable Systems",
      desc: "Improve security, stability, and application reliability.",
      iconColor: "#a855f7",
    },
    {
      icon: "/images/services/website/clean_code.webp",
      title: "Continuous Support & Maintenance",
      desc: "Regular updates, monitoring, and long-term technical support.",
      iconColor: "#34d399",
    },
  ],
  floatingIcons: [
    {
      image: "/images/services/website/nodejs.webp",
      top: "16%",
      right: "6%",
      containerSize: 80,
      iconSize: 60,
      glowColor: "rgba(59,130,246,0.35)",
      iconGlow: "rgba(59,130,246,0.9)",
    },
    {
      image: "/images/services/website/reactjs.svg",
      top: "8%",
      right: "14%",
      containerSize: 70,
      iconSize: 50,
      glowColor: "rgba(59,130,246,0.35)",
      iconGlow: "rgba(59,130,246,0.9)",
    },
    {
      image: "/images/services/website/python.svg",
      top: "20%",
      right: "20%",
      containerSize: 60,
      iconSize: 40,
      glowColor: "rgba(59,130,246,0.35)",
      iconGlow: "rgba(59,130,246,0.9)",
    },
  ],
};

export const testingSolutionsAndProcessData = {
  buildSubtitle: "WHAT WE BUILD",
  buildTitle: "Testing & Maintenance Solutions for Every Business Need",
  buildCards: [
    {
      title: "Mobile App Testing Services",
      desc: "Ensure smooth performance and functionality across Android and iOS applications.",
      icon: LuSmartphone,
      colorClass: "text-[#22d3ee]",
      bgClass: "bg-[#22d3ee]/10",
      borderClass: "border-[#22d3ee]/20 hover:border-[#22d3ee]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(34,211,238,0.25)]",
    },
    {
      title: "Enterprise Software Maintenance",
      desc: "Maintain secure, scalable, and high-performing enterprise applications.",
      icon: LuBuilding2,
      colorClass: "text-[#3b82f6]",
      bgClass: "bg-[#3b82f6]/10",
      borderClass: "border-[#3b82f6]/20 hover:border-[#3b82f6]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(59,130,246,0.25)]",
    },
    {
      title: "E-Commerce Application Testing",
      desc: "Improve shopping experiences with reliable and performance-focused testing.",
      icon: LuShoppingBag,
      colorClass: "text-[#a855f7]",
      bgClass: "bg-[#a855f7]/10",
      borderClass: "border-[#a855f7]/20 hover:border-[#a855f7]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(168,85,247,0.25)]",
    },
    {
      title: "On-Demand App Maintenance Solutions",
      desc: "Ensure stable performance for booking, delivery, and service-based applications.",
      icon: LuClock,
      colorClass: "text-[#06b6d4]",
      bgClass: "bg-[#06b6d4]/10",
      borderClass: "border-[#06b6d4]/20 hover:border-[#06b6d4]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(6,182,212,0.25)]",
    },
    {
      title: "FinTech Application Testing Services",
      desc: "Strengthen security, compliance, and reliability for fintech platforms.",
      icon: LuWallet,
      colorClass: "text-[#6366f1]",
      bgClass: "bg-[#6366f1]/10",
      borderClass: "border-[#6366f1]/20 hover:border-[#6366f1]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(99,102,241,0.25)]",
    },
    {
      title: "Custom Testing & Maintenance Solutions",
      desc: "Tailored testing and support services designed for unique business requirements.",
      icon: LuWrench,
      colorClass: "text-[#d946ef]",
      bgClass: "bg-[#d946ef]/10",
      borderClass: "border-[#d946ef]/20 hover:border-[#d946ef]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(217,70,239,0.25)]",
    },
  ],
  processSubtitle: "Our Proven Testing & Maintenance Process",
  processTitle: "Our Strategic Testing & Maintenance Approach",
  processSteps: [
    {
      step: "01",
      title: "Requirement Analysis",
      description: "We understand your application goals, system requirements, and testing objectives.",
      icon: LuUsers,
    },
    {
      step: "02",
      title: "Testing Strategy & Planning",
      description: "We create a structured testing roadmap based on performance, security, and functionality needs.",
      icon: LuClipboardList,
    },
    {
      step: "03",
      title: "Test Case Design",
      description: "We prepare detailed test scenarios, workflows, and validation processes for accurate testing.",
      icon: LuFileText,
    },
    {
      step: "04",
      title: "Application Testing Execution",
      description: "We perform functional, performance, security, and compatibility testing across platforms.",
      icon: LucidePlayCircle,
    },
    {
      step: "05",
      title: "Bug Tracking & Optimization",
      description: "We identify issues, optimize application performance, and improve overall system stability.",
      icon: LuBug,
    },
    {
      step: "06",
      title: "Maintenance & Ongoing Support",
      description: "We provide continuous monitoring, updates, and long-term maintenance support for reliable performance.",
      icon: LuRefreshCw,
    },
  ],
  benefitsSubtitle: "Why Choose Our Testing & Maintenance Services?",
  benefitsTitle: "Why Businesses Choose Testing & Maintenance Services",
  benefits: [
    "Improved Application Reliability",
    "Faster Issue Detection & Resolution",
    "Better Security & Performance",
    "Cross-Platform Compatibility Testing",
    "Continuous Monitoring & Support",
    "Scalable Maintenance Solutions",
    "Reduced Downtime & System Errors",
  ],
  logoImage: "/images/serviceBanners/TestingLatest.webp",
  whiteLogoImage: "/images/serviceBanners/TestingLatestW.webp",
};

export const testingPortfolioData = {
  subtitle: "OUR WORK",
  title: "Testing Project We're Proud Of",
  buttonText: "View All Projects",
  buttonLink: "#all-projects",
  apps: [
    {
      title: "Foodie Express",
      badge: "Food Delivery App",
      badgeColor: "bg-[#00d2ff]/10 text-[#00d2ff] border-[#00d2ff]/20",
      desc: "A fast and intuitive food delivery app with real-time tracking, secure payments & smooth experience.",
      downloads: "100K+",
      rating: "4.8",
      image: "/images/MobileAppDevelopment/foodie_express.png",
    },
    {
      title: "FinMate",
      badge: "FinTech App",
      badgeColor: "bg-[#a855f7]/10 text-[#a855f7] border-[#a855f7]/20",
      desc: "A personal finance app to manage expenses, budgets & investments with advanced analytics.",
      downloads: "250K+",
      rating: "4.9",
      image: "/images/MobileAppDevelopment/finmate.png",
    },
    {
      title: "TravelGo",
      badge: "Travel App",
      badgeColor: "bg-[#3b82f6]/10 text-[#3b82f6] border-[#3b82f6]/20",
      desc: "A travel booking app for flights, hotels & packages with best deals and easy bookings.",
      downloads: "500K+",
      rating: "4.7",
      image: "/images/MobileAppDevelopment/travelgo.png",
    },
    {
      title: "TravelGo",
      badge: "Travel App",
      badgeColor: "bg-[#3b82f6]/10 text-[#3b82f6] border-[#3b82f6]/20",
      desc: "A travel booking app for flights, hotels & packages with best deals and easy bookings.",
      downloads: "500K+",
      rating: "4.7",
      image: "/images/MobileAppDevelopment/travelgo.png",
    },
  ],
};
export const testingMaintenanceAboutData = [
  {
    title: "Reliable Software Testing for Stable Digital Products",
    content: (
      <>
        Modern businesses rely on stable and error-free digital platforms to maintain customer trust and operational efficiency. Professional{" "}
        <b className="text-text-primary font-semibold">software testing services</b> help organizations improve software quality, reduce technical risks, and ensure smooth digital
        experiences across web, mobile, and enterprise environments. A trusted <b className="text-text-primary font-semibold">qa testing company</b> focuses on identifying
        usability gaps, functionality issues, and system inconsistencies before they impact end users.
      </>
    ),
  },

  {
    title: "Advanced Quality Assurance & Security Validation",
    content: (
      <>
        Effective software validation requires a structured testing approach supported by modern QA practices and real-time monitoring. Businesses often work with providers
        offering{" "}
        <a href="https://tychotechnologies.com/automated-regression-testing-india" className="text-cyan-400 underline hover:text-cyan-300">
          automated regression testing services
        </a>{" "}
        to validate software updates, maintain workflow consistency, and minimize deployment risks during continuous development cycles. Companies also rely on professional{" "}
        <a href="https://tychotechnologies.com/security-testing-india" className="text-cyan-400 underline hover:text-cyan-300">
          security testing services
        </a>{" "}
        to evaluate application vulnerabilities, strengthen data protection, and improve compliance standards for secure digital operations.
      </>
    ),
  },

  {
    title: "Long-Term Application Maintenance & Technical Support",
    content: (
      <>
        Growing businesses require continuous optimization and technical assistance to maintain software efficiency over time. Reliable{" "}
        <b className="text-text-primary font-semibold">application maintenance services</b> help organizations manage software upgrades, improve system adaptability, and support
        evolving business requirements. From SaaS platforms to enterprise applications, businesses benefit from proactive monitoring, technical troubleshooting, and structured
        maintenance workflows that support uninterrupted digital performance.
      </>
    ),
  },

  {
    title: "Scalable Testing & Maintenance Services in India",
    content: (
      <>
        The demand for dependable software support and maintenance solutions continues to increase across industries including fintech, healthcare, eCommerce, logistics, and
        enterprise platforms. Businesses searching for long-term technical expertise often choose a trusted{" "}
        <b className="text-text-primary font-semibold">software testing company in india</b> for experienced QA teams and scalable testing strategies. Companies also partner with
        professional <b className="text-text-primary font-semibold">application maintenance services india</b> providers to improve software longevity, strengthen digital
        reliability, and support future business scalability.
      </>
    ),
  },
];
